/**
 * pago-orden-logic.ts
 * Lógica pura para visibilidad y estado de pago de órdenes.
 * Sin dependencias React — facilita testing con fast-check.
 */

import type { OrdenListItem, PagoOrden } from "@/hooks/use-ordenes";

/**
 * Determina si una orden puede ser pagada.
 * Condiciones: no pagada y no en estado BAJA.
 */
export function canPay(orden: Pick<OrdenListItem, "pagada" | "estado">): boolean {
  return !orden.pagada && orden.estado !== "BAJA";
}

/**
 * Determina si se puede anular el pago de una orden.
 * Condiciones: pagada y no en estado BAJA.
 */
export function canCancelPayment(orden: Pick<OrdenListItem, "pagada" | "estado">): boolean {
  return orden.pagada && orden.estado !== "BAJA";
}

/**
 * Retorna la clave i18n del estado de pago de una orden.
 */
export function getPaymentStatusKey(orden: Pick<OrdenListItem, "pagada">): string {
  return orden.pagada ? "ordenes.pago.paid" : "ordenes.pago.pending";
}

/**
 * Determina si se puede registrar un nuevo pago en la orden.
 * Condiciones: no en BAJA, no tiene tipo_cobro FACTURA, y saldo > 0.
 */
export function canPagarOrden(
  orden: Pick<OrdenListItem, "estado" | "tipo_cobro"> & { saldo_pendiente?: string; total?: string }
): boolean {
  if (orden.estado === "BAJA") return false;
  if (orden.tipo_cobro === "FACTURA") return false;
  const saldo = parseFloat(orden.saldo_pendiente ?? orden.total ?? "0");
  return saldo > 0;
}

/**
 * Determina si se puede anular un pago específico.
 * Condiciones: pago activo y orden no en BAJA.
 */
export function canAnularPagoOrden(
  pago: Pick<PagoOrden, "is_active">,
  orden: Pick<OrdenListItem, "estado">
): boolean {
  return pago.is_active && orden.estado !== "BAJA";
}

/**
 * Determina si una orden tiene pagos parciales en curso.
 * Condición: tipo_cobro === null Y (al menos un pago activo O total_pagado > 0).
 * Útil para mostrar el badge "Pago parcial" y el resumen financiero.
 *
 * Acepta dos formas de detectar pagos:
 * - `pagos`: lista de PagoOrden (OrdenDetailSheet)
 * - `totalPagado`: string del campo total_pagado (tabla de órdenes)
 */
export function tienePagoParcial(
  orden: Pick<OrdenListItem, "tipo_cobro">,
  pagos: Pick<PagoOrden, "is_active">[],
  totalPagado?: string
): boolean {
  if (orden.tipo_cobro !== null) return false;
  if (pagos.some((p) => p.is_active)) return true;
  if (totalPagado !== undefined) return parseFloat(totalPagado) > 0;
  return false;
}

/**
 * Determina si debe mostrarse el resumen financiero (total/pagado/saldo).
 * Se muestra cuando:
 *   - tipo_cobro === "DIRECTO"  (comportamiento existente), O
 *   - tipo_cobro === null Y hay al menos un pago activo (pagos parciales)
 */
export function debeMostrarResumenPagos(
  orden: Pick<OrdenListItem, "tipo_cobro">,
  pagos: Pick<PagoOrden, "is_active">[]
): boolean {
  if (orden.tipo_cobro === "DIRECTO") return true;
  if (orden.tipo_cobro === null) return pagos.some((p) => p.is_active);
  return false;
}

/**
 * Calcula el total de una orden aplicando descuentos.
 * total = precio_bruto - descuento_lempiras - (precio_bruto * descuento_porcentaje / 100)
 * Retorna 0 si el resultado es negativo.
 */
export function calcularTotalOrden(
  orden: Pick<OrdenListItem, "descuento_porcentaje" | "descuento_lempiras"> & {
    precio_bruto: number;
  }
): number {
  const descPct = (orden.precio_bruto * orden.descuento_porcentaje) / 100;
  const total = orden.precio_bruto - descPct - orden.descuento_lempiras;
  return Math.max(0, total);
}

export interface ResultadoVueltoOrden {
  montoAplicado: number;
  vuelto: number;
  restante: number;
}

/**
 * Calcula el vuelto dado el saldo pendiente y el monto recibido.
 * - montoAplicado: cuánto se aplica al saldo (min(monto, saldo))
 * - vuelto: excedente devuelto al paciente
 * - restante: saldo que queda después del pago
 */
export function calcularVueltoOrden(
  saldoPendiente: number,
  montoRecibido: number
): ResultadoVueltoOrden {
  const s = Math.max(0, saldoPendiente);
  const m = Math.max(0, montoRecibido);
  const montoAplicado = Math.min(m, s);
  return {
    montoAplicado,
    vuelto: Math.max(m - s, 0),
    restante: Math.max(s - m, 0),
  };
}
